import { Box, Card, withStyles, formatDate, MaterialSymbol, Pagination, capitalizeFirstLetter } from 'vopay';
import type { AccountTransactionReports, AccountTransactionReportType, TransactionFailureReason } from 'vopay';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useParams, useHistory } from 'react-router-dom';
import { usePermissions } from '@/providers/Permissions.provider';
import { useClientData } from '@/providers/ClientData.provider';

import useFetchSubscriptionDetail from '@/hooks/useApi/useFetchSubscriptionDetail';
import usePostEditReport from '@/hooks/useApi/usePostEditReport';
import usePostDownloadReport from '@/hooks/useApi/usePostDownloadReport';
import ReportCreationDrawer from '../Dialogue/ReportCreation/ReportCreationDrawer';
import useFileDownload from '@/hooks/useFiledDownload';
import useFetchSubscriptions from '@/hooks/useApi/useFetchSubscriptions';

import ReportDetailHistoryTable from './ReportDetailHistoryTable';
import EditReportItem from './EditReportItem';

import styles from './ReportDetailsPage.module.scss';
import { Title, Button, UtilButton, NoFillButton } from 'UI';
import { ReportDetail } from '../types';
import { useDataFetchIndicator } from '@/hooks';

const cn = withStyles(styles);

type RouteParams = {
  ReportID: string;
};

const ReportDetailsContainer = () => {
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'Reports' });
  const { downloadXlsxFileType } = useFileDownload();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { permissions } = usePermissions();
  const { ReportID } = useParams<RouteParams>();
  const [reportsResponse, fetchReports] = useFetchSubscriptions({});
  const { response } = useClientData();
  const [hasReportEditted, setHasReportEditted] = useState(false);
  const [fieldBeingEdited, setFieldBeingEdited] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [subscriptionDetail, fetchSubscriptionDetail] = useFetchSubscriptionDetail({});
  const [subscription, setSubscription] = useState<ReportDetail>({
    ID: '',
    ReportName: '',
    RecipientEmails: '',
    Description: '',
    IsActive: '',
    ScheduleType: '',
    Frequency: '',
    DateFormat: '',
    DateAdded: '',
    FromDate: '',
    ToDate: '',
    LastModified: '',
    PaymentMethods: '',
    IncludeSubAccounts: false,
    IncludeGraph: false,
    DateRangeOption: '',
    AccountTransactionReportType: '',
    TransactionReportFailureReasons: [],
  });
  const [reportList, setReportList] = useState<AccountTransactionReports[]>([]);
  const [, , , postEditSubscriptionStatus] = usePostEditReport({});
  const [, , downloadReport] = usePostDownloadReport({});
  const [isOneTimeReport, setIsOneTimeReport] = useState(false);

  // pagination
  const [currentPage, setCurrentPage] = useState(0);
  const PER_PAGE = 10;
  const offset = currentPage * PER_PAGE;

  const pageCount = Math.ceil(reportList.length / PER_PAGE);
  const handlePageClick = ({ selected: selectedPage }: { [idx: string]: number }) => {
    setCurrentPage(selectedPage);
  };

  useEffect(() => {
    fetchReports({
      PortalUser: response.LoginUserName,
    });
  }, []);

  const accountTransactionReportTypeMap: Record<AccountTransactionReportType, string> = {
    ApprovalActivityReport: t('ReportDetails.AccountTransactionReportType.approvalActivity', {
      defaultValue: 'Approval Activity',
    }),
    AccountTransactionHistoryReport: t('ReportDetails.AccountTransactionReportType.accountTransactionHistory', {
      defaultValue: 'Account Transaction History',
    }),
    AccountTransactionSummary: t('ReportDetails.AccountTransactionReportType.accountTransactionSummary'),
    AccountingSoftwareActivityReport: t('ReportDetails.AccountTransactionReportType.accountingSoftwareActivityReport'),
    EndOfDayBalance: t('ReportDetails.AccountTransactionReportType.endOfDayBalance'),
    InterestReport: t('ReportDetails.AccountTransactionReportType.interestReport'),
    TransactionsPerPaymentRail: t('ReportDetails.AccountTransactionReportType.transactionsPerPaymentRail'),
    TransactionSuccessAndFailureAnalysisReport: t(
      'ReportDetails.AccountTransactionReportType.transactionSuccessAndFailureAnalysisReport',
    ),
    InvoiceReport: t('ReportDetails.AccountTransactionReportType.invoiceReport'),
    GLCodeSummaryReport: t('ReportDetails.AccountTransactionReportType.glCodeSummaryReport'),
    GLCodeTransactionSummary: t('ReportDetails.AccountTransactionReportType.glCodeTransactionSummary'),
    TransactionSummaryByCode: t('ReportDetails.AccountTransactionReportType.transactionSummaryByCode'),
    TransactionCodeSummary: t('ReportDetails.AccountTransactionReportType.transactionCodeSummaryReport'),
    TransactionFailureDetailReport: t('ReportDetails.AccountTransactionReportType.transactionFailureDetailReport'),
    PaymentMethodChangeLogReport: t('ReportDetails.AccountTransactionReportType.paymentMethodChangeLogReport', {
      defaultValue: 'Payment Method Change Log',
    }),
  };

  const reportDetails = [
    {
      key: 'recipientEmails',
      label: t('ReportDetails.Fields.recipientEmails'),
      value: subscription.RecipientEmails,
      canEdit: false,
    },
    {
      label: t('ReportDetails.Fields.reportName'),
      value: subscription.ReportName,
      canEdit: true,
    },
    {
      label: t('ReportDetails.Fields.description'),
      value: subscription.Description,
      canEdit: true,
    },
    {
      label: t('ReportDetails.Fields.reportType'),
      value: accountTransactionReportTypeMap[subscription.AccountTransactionReportType as AccountTransactionReportType],
      canEdit: false,
    },
    {
      key: 'paymentMethods',
      label: t('ReportDetails.Fields.paymentMethods'),
      value: subscription.PaymentMethods,
      canEdit: false,
    },
    {
      label: t('ReportDetails.Fields.dateFormat'),
      value: subscription.DateFormat,
      canEdit: true,
    },
    ...(!isOneTimeReport
      ? [
          {
            label: t('ReportDetails.Fields.frequency'),
            value: capitalizeFirstLetter(subscription.Frequency),
            canEdit: true,
          },
        ]
      : []),
    {
      key: 'failureReason',
      label: t('ReportDetails.Fields.failureReason'),
      value: subscription.TransactionReportFailureReasons,
      canEdit: false,
    },
    {
      label: t('ReportDetails.Fields.createdOn'),
      value: subscription.DateAdded ? formatDate(subscription.DateAdded) : '',
      canEdit: false,
    },
  ];

  useEffect(() => {
    fetchSubscriptionDetail({
      PortalUser: response.LoginUserName,
      SubscriptionID: ReportID,
    });
  }, [ReportID]);

  useEffect(() => {
    if (hasReportEditted) {
      setFieldBeingEdited('');
      toast.success(<strong>{t('ReportTable.ToastMessage.updated')}</strong>);
      fetchSubscriptionDetail({
        PortalUser: response.LoginUserName,
        SubscriptionID: ReportID,
      });
      setHasReportEditted(false);
    }
  }, [hasReportEditted]);

  useEffect(() => {
    if (subscriptionDetail.Success) {
      setSubscription({
        ...subscriptionDetail.AccountSubscriptionDetail,
        PaymentMethods: Object.values(subscriptionDetail.AccountSubscriptionDetail.PaymentRails || {}).join(','),
        TransactionReportFailureReasons:
          subscriptionDetail.AccountSubscriptionDetail.FilterParams.TransactionReportFailureReasons,
        FromDate: new Date(subscriptionDetail.AccountSubscriptionDetail.FromDate).getTime().toString(),
        ToDate: new Date(subscriptionDetail.AccountSubscriptionDetail.ToDate).getTime().toString(),
      });
      setReportList(
        Object.values(subscriptionDetail.AccountSubscriptionDetail?.AccountTransactionReports).filter(
          (e) => e.IsAvailable === '1',
        ),
      );
      setIsOneTimeReport(subscriptionDetail.AccountSubscriptionDetail?.ScheduleType === 'one-time');
    }
  }, [subscriptionDetail]);

  return (
    <div className={cn('reportDetailsPage')}>
      {subscriptionDetail.Success ? (
        <Box flx="col" gapSize="16px">
          <UtilButton iconLeft="chevron_left" size="large" onClick={() => history.goBack()}>
            {t('ReportDetails.ButtonText.back')}
          </UtilButton>
          <Box flx="col" gap="common">
            <div className={cn('pageHeader')}>
              <div className={cn('pageHeaderLeft')}>
                <Title>{subscription.ReportName}</Title>
                {!isOneTimeReport &&
                  (subscription.IsActive === '1' ? (
                    <div className={cn('state', 'activeState')}>
                      <MaterialSymbol icon="check_circle" size="20px" fillAmount={1} color="#048811" />
                      <span>{t('ReportDetails.ButtonText.active')}</span>
                    </div>
                  ) : (
                    <div className={cn('state', 'inactiveState')}>
                      <MaterialSymbol icon="do_not_disturb_on" size="20px" fillAmount={1} color="#2D2D2D33" />
                      <span>{t('ReportDetails.ButtonText.inactive')}</span>
                    </div>
                  ))}
              </div>
              <div className={cn('pageHeaderLinks')}>
                {!isOneTimeReport && (
                  <>
                    {subscription.IsActive === '0' && (
                      <Button
                        onClick={async () => {
                          try {
                            setDataFetchingInfo(true);
                            const res = await postEditSubscriptionStatus({
                              PortalUser: response.LoginUserName || '',
                              SubscriptionID: subscription.ID || '',
                              IsActive: subscription.IsActive === '1' ? '0' : '1',
                            });
                            if (res.Success) {
                              toast.success(<strong>{t('ReportTable.ToastMessage.reportActivated')}</strong>);
                              fetchSubscriptionDetail({
                                PortalUser: response.LoginUserName,
                                SubscriptionID: ReportID,
                              });
                              setHasReportEditted(false);
                            } else {
                              await alertDialog.caution(
                                t('ReportTable.DialogueMessage.activateFail', {
                                  label: subscription.ReportName,
                                }),
                                `${res.ErrorMessage}`,
                                {
                                  confirmLabel: 'Okay',
                                },
                              );
                            }
                          } catch (err) {
                            toast.neutral(
                              t('ReportTable.ToastMessage.activateFail', {
                                label: err,
                              }),
                            );
                          } finally {
                            setDataFetchingInfo(false);
                          }
                        }}
                      >
                        {t('ReportDetails.ButtonText.activate')}
                      </Button>
                    )}
                  </>
                )}
                {subscription.IsActive === '1' && (
                  <>
                    {permissions.CreateTransactionReport && (
                      <UtilButton
                        iconLeft="file_copy"
                        size="large"
                        onClick={() => setIsDrawerOpen(true)}
                        disabled={!!fieldBeingEdited}
                      >
                        {t('ReportDetails.ButtonText.clone')}
                      </UtilButton>
                    )}

                    <ReportCreationDrawer
                      isOpen={isDrawerOpen}
                      onClose={() => setIsDrawerOpen(false)}
                      onSuccess={async (id) => {
                        toast.success(
                          t('ReportTable.ToastMessage.cloneSuccess', {
                            label: isOneTimeReport
                              ? t('ReportDetails.ScheduleType.oneTimeTitleCase')
                              : t('ReportDetails.ScheduleType.subscriptionTitleCase'),
                          }),
                        );
                        const decision = await alertDialog.success(
                          t('ReportTable.DialogueMessage.Clone.successMessage', {
                            label: isOneTimeReport
                              ? t('ReportDetails.ScheduleType.oneTimeTitleCase')
                              : t('ReportDetails.ScheduleType.subscriptionTitleCase'),
                          }),
                          t('ReportTable.DialogueMessage.Clone.successQuestion', {
                            label: isOneTimeReport
                              ? t('ReportDetails.ScheduleType.oneTime')
                              : t('ReportDetails.ScheduleType.subscription'),
                          }),
                          {
                            confirmLabel: t('ReportTable.DialogueMessage.Clone.confirmLabel', {
                              label: isOneTimeReport
                                ? t('ReportDetails.ScheduleType.oneTime')
                                : t('ReportDetails.ScheduleType.subscription'),
                            }),
                            abortLabel: t('ReportTable.DialogueMessage.Clone.abortLabel'),
                          },
                        );
                        if (decision === 'confirm') {
                          history.push(`/reports/${id}`);
                        }
                      }}
                      title={isOneTimeReport ? t('ReportDetails.cloneReport') : t('ReportDetails.cloneSubscription')}
                      subscriptions={reportsResponse.Subscriptions}
                      disabledFields={['ScheduleType']}
                      initialFields={{
                        ...subscription,
                        PaymentMethods: subscription.PaymentMethods.split(',').filter(Boolean),
                        TransactionReportFailureReasons: subscription.TransactionReportFailureReasons,
                      }}
                    />
                    {isOneTimeReport && reportList.length !== 0 && (
                      <UtilButton
                        iconLeft="download"
                        size="large"
                        onClick={async () => {
                          const { Success, ReportFile, ReportName } = await downloadReport({
                            ReportID: reportList[0].ID,
                          });
                          if (Success) {
                            downloadXlsxFileType(ReportFile, ReportName);
                          }
                        }}
                      >
                        {t('ReportDetails.ButtonText.download')}
                      </UtilButton>
                    )}
                  </>
                )}
              </div>
            </div>
            <Card style={{ padding: '10px 12px' }}>
              {reportDetails.map(({ key = '', label, value, canEdit }) => {
                let valueNode = <div className={styles.value}>{value ? value : t('ReportDetails.notApplicable')}</div>;
                if (key === 'paymentMethods' && value) {
                  valueNode = (
                    <div className={cn('value', 'paymentMethodValue')}>
                      {(value as string).split(',').map((item) => (
                        <div key={item} className={cn('card')}>
                          {item}
                        </div>
                      ))}
                    </div>
                  );
                }
                if (key === 'failureReason') {
                  if (value) {
                    valueNode = (
                      <div className={styles.value}>
                        {(value as TransactionFailureReason[]).map(capitalizeFirstLetter).join(', ')}
                      </div>
                    );
                  }
                }
                if (key === 'recipientEmails') {
                  if (value) {
                    valueNode = <div className={styles.value}>{(value as string).split(',').join(', ')}</div>;
                  }
                }
                return (
                  <div
                    key={label}
                    className={cn('reportDetails', fieldBeingEdited && fieldBeingEdited !== label && 'disabled')}
                  >
                    <div className={cn('label')}>{label}</div>
                    <div className={cn('reportDetailsRight')}>
                      {!isOneTimeReport && canEdit ? (
                        <EditReportItem
                          report={subscription}
                          subscriptions={reportsResponse.Subscriptions}
                          reportItemLabel={label}
                          reportItemValue={value as string}
                          setEditReport={setHasReportEditted}
                          setReportBeingEdited={setFieldBeingEdited}
                        />
                      ) : (
                        <>{valueNode}</>
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
            {!isOneTimeReport && (
              <Card>
                {reportList.length === 0 ? (
                  <Box ai="center" jc="center" gap="sm">
                    <MaterialSymbol icon="description" size="62px" fillAmount={1} color="rgb(45, 45, 45, 0.75)" />
                    <Box flx="col" gapSize="5px">
                      <Title level="3">{t('ReportDetails.reportHistory')}</Title>
                      <span style={{ fontSize: '17px' }}>{t('ReportDetails.noReportAvailable')}</span>
                    </Box>
                  </Box>
                ) : (
                  <div className={fieldBeingEdited ? cn('tableContainer', 'disabled') : undefined}>
                    <ReportDetailHistoryTable reports={reportList.slice(offset, offset + PER_PAGE)} />
                    <Box width="100%" jc="center" mt="lg">
                      {pageCount > 1 && <Pagination pageCount={pageCount} handlePageClick={handlePageClick} />}
                    </Box>
                  </div>
                )}
              </Card>
            )}
            {!isOneTimeReport && subscription.IsActive === '1' ? (
              <NoFillButton
                colorScheme="destructive"
                onClick={async () => {
                  try {
                    const decision = await alertDialog.caution(
                      t('ReportTable.DialogueMessage.deactivateSubscription', {
                        label: isOneTimeReport ? 'One-Time Report' : 'Subscription',
                      }),
                      t('ReportTable.DialogueMessage.deactivateSubscriptionDescription', {
                        reportName: subscription.ReportName,
                        recipientEmails: subscription.RecipientEmails.split(',')[0],
                      }),
                      {
                        confirmLabel: t('ReportTable.ActionStatus.deactivate'),
                        abortLabel: t('ReportTable.ActionStatus.cancel'),
                      },
                    );

                    if (decision === 'confirm') {
                      setDataFetchingInfo(true);

                      const res = await postEditSubscriptionStatus({
                        PortalUser: response.LoginUserName || '',
                        SubscriptionID: subscription.ID || '',
                        IsActive: subscription.IsActive === '1' ? '0' : '1',
                      });

                      if (res.Success) {
                        toast.neutral(<strong>{t('ReportTable.ToastMessage.reportDeactivated')}</strong>);
                        fetchSubscriptionDetail({
                          PortalUser: response.LoginUserName,
                          SubscriptionID: ReportID,
                        });
                        setHasReportEditted(false);
                      } else {
                        toast.neutral(
                          t('ReportTable.ToastMessage.deactivateFail', {
                            label: res.ErrorMessage,
                          }),
                        );
                        await alertDialog.caution(
                          t('ReportTable.DialogueMessage.deactivateFail', {
                            label: subscription.ReportName,
                          }),
                          `${res.ErrorMessage}`,
                          {
                            confirmLabel: 'Okay',
                          },
                        );
                      }
                    }
                  } catch (err) {
                    toast.neutral(
                      t('ReportTable.ToastMessage.deactivateFail', {
                        label: err,
                      }),
                    );

                    const msg = err instanceof Error ? err.message : null;

                    await alertDialog.caution(
                      t('ReportTable.DialogueMessage.deactivateFail', {
                        label: subscription.ReportName,
                      }),
                      msg,
                      {
                        confirmLabel: 'Okay',
                      },
                    );
                  } finally {
                    setDataFetchingInfo(false);
                  }
                }}
                disabled={!!fieldBeingEdited}
                style={{ width: '132px' }}
              >
                {t('ReportDetails.ButtonText.deactivate')}
              </NoFillButton>
            ) : null}
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </div>
  );
};

export default ReportDetailsContainer;
